/**
 * DataRangers 埋点工具 - H5 版本
 * @description 适配 H5 环境的 DataRangers SDK 封装，与小程序版本接口保持一致
 */

import DataRangers from '@datarangers/sdk-javascript'
import { useConfigStore } from '@/stores/modules/config.js'

// SDK 初始化状态
let isInitialized = false

// 事件队列：SDK 未初始化时暂存，初始化完成后自动补发
const pendingEvents = []
const pendingConfigs = []

/**
 * 初始化 DataRangers SDK
 * @param {Object} options - 初始化配置
 * @param {number} options.appId - 应用 ID
 * @param {string} options.channel - 上报区域：cn(国内)、sg(新加坡)、va(美东)，默认 cn
 * @param {boolean} options.log - 是否打印日志
 * @param {boolean} options.autotrack - 是否开启自动采集
 */
export function initRangers(options = {}) {
  const { channel, log = false } = options

  if (isInitialized) {
    console.warn('[Rangers] SDK 已初始化，请勿重复调用')
    return
  }

  // 验证 channel 参数有效性
  const validChannels = ['cn', 'sg', 'va']
  const reportChannel = channel && validChannels.includes(channel) ? channel : 'cn'
  const configStore = useConfigStore()
  try {
    DataRangers.init({
      app_id: Number(configStore.otherConfig?.trackingPoint?.appId),
      channel: reportChannel,
      log: log || true,
      autotrack: true
    })
    const config = {
      exhibitionId: getStorageItem('exhibitionId', ''),
      client: 'itinerary-app',
      evtParams: {
        client: 'itinerary-app',
      },
    }
    const appId = getStorageItem('appId', '')
    if (appId) config.appId = appId

    DataRangers.config(config)

    DataRangers.send()
    isInitialized = true

    // 补发初始化前排队的配置和事件
    flushPendingConfigs()
    flushPendingEvents()

    if (log) console.log('[Rangers] SDK 初始化成功')
  } catch (error) {
    console.error('[Rangers] 初始化失败:', error)
  }
}

// 导出 SDK 实例
export const $Rangers = DataRangers

/**
 * 从 localStorage 获取数据（H5 替代 wx.getStorageSync）
 * @param {string} key - 存储键名
 * @param {any} defaultValue - 默认值
 * @returns {any}
 */
function getStorageItem(key, defaultValue = '') {
  try {
    const data = JSON.parse(localStorage.getItem('user') || '{}')
    if (key === 'userAccountId') {
      // 与小程序端字段名保持一致
      return data?.exhibitorInfo?.userId || defaultValue
    } else if (key === 'exhibitionId') {
      return data?.exhibitionId || defaultValue
    } else {
      return data?.buriedPointConfig?.[key] || defaultValue
    }
  } catch (e) {
    return defaultValue
  }
}

/**
 * 设置 DataRangers 公有属性
 * @param {Object} publicParams - 公共参数
 * @param {Object} evtParams - 事件参数
 */
export function $RangersConfig(publicParams = {}, evtParams = {}) {
  if (!isInitialized) {
    console.warn('[Rangers] SDK 未初始化，配置已加入队列')
    pendingConfigs.push({ publicParams, evtParams })
    return
  }

  DataRangers.config({
    ...publicParams,
    evtParams: {
      client: 'itinerary-app',
      subjectType: getStorageItem('subjectType', 'VISITOR'),
      ...evtParams,
    },
  })
}

/**
 * 自定义事件埋点
 * @param {string|Array} event - 事件名称或事件数组
 * @param {Object} evtParams - 事件参数
 */
export function rangersEvent(event = '', evtParams = {}) {
  if (!isInitialized) {
    console.warn('[Rangers] SDK 未初始化，事件已加入队列:', event)
    pendingEvents.push({ event, evtParams })
    return
  }

  try {
    if (typeof event === 'string') {
      const params = {
        subjectType: getStorageItem('subjectType', 'VISITOR'),
        useraccount_id: getStorageItem('userAccountId', ''),  // 与小程序端字段名保持一致
        ...evtParams
      }
      DataRangers.event(event, params)
    } else if (Array.isArray(event)) {
      DataRangers.event(event)
    }
  } catch (error) {
    console.error('[Rangers] 事件上报失败:', error)
  }
}

/**
 * 补发排队中的事件
 */
function flushPendingEvents() {
  if (pendingEvents.length === 0) return
  console.log(`[Rangers] 正在补发 ${pendingEvents.length} 个排队事件...`)
  while (pendingEvents.length > 0) {
    const { event, evtParams } = pendingEvents.shift()
    try {
      if (typeof event === 'string') {
        const params = {
          subjectType: getStorageItem('subjectType', 'VISITOR'),
          useraccount_id: getStorageItem('userAccountId', ''),
          ...evtParams
        }
        DataRangers.event(event, params)
      } else if (Array.isArray(event)) {
        DataRangers.event(event)
      }
    } catch (error) {
      console.error('[Rangers] 补发事件失败:', event, error)
    }
  }
}

/**
 * 补发排队中的配置
 */
function flushPendingConfigs() {
  if (pendingConfigs.length === 0) return
  console.log(`[Rangers] 正在补发 ${pendingConfigs.length} 个排队配置...`)
  while (pendingConfigs.length > 0) {
    const { publicParams, evtParams } = pendingConfigs.shift()
    try {
      DataRangers.config({
        ...publicParams,
        evtParams: {
          client: 'itinerary-app',
          subjectType: getStorageItem('subjectType', 'VISITOR'),
          ...evtParams,
        },
      })
    } catch (error) {
      console.error('[Rangers] 补发配置失败:', error)
    }
  }
}


export default {
  $Rangers,
  $RangersConfig,
  rangersEvent,
  init: initRangers
}
